import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Link,
} from '@mui/material';
import {
  formatTimezoneAbbrevAndOffset,
  getAllIanaTimeZones,
  getTimezoneSearchHaystack,
} from '../utils/dateUtils';

export interface TimezoneSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  /** Effective zone currently used for display. */
  currentTimezone: string;
  browserTimezone: string;
  /** Stored override, or null = following browser. */
  timezoneOverride: string | null;
  onSelect: (tz: string | 'browser') => void;
}

const ZONE_OPTIONS = getAllIanaTimeZones();

type TzAutocompleteOption = {
  id: string;
  label: string;
  value: string | 'browser';
  searchHaystack: string;
};

const TimezoneSelectorDialog: React.FC<TimezoneSelectorDialogProps> = ({
  open,
  onClose,
  currentTimezone,
  browserTimezone,
  timezoneOverride,
  onSelect,
}) => {
  const [input, setInput] = useState('');

  const browserOption = useMemo(
    (): TzAutocompleteOption => ({
      id: '__browser__',
      label: `Browser default — ${browserTimezone} (${formatTimezoneAbbrevAndOffset(browserTimezone)})`,
      value: 'browser',
      searchHaystack: `browser default system ${getTimezoneSearchHaystack(browserTimezone)}`.toLowerCase(),
    }),
    [browserTimezone]
  );

  const allOptions = useMemo((): TzAutocompleteOption[] => {
    const zones = ZONE_OPTIONS.map((z) => ({
      id: z,
      label: `${z} (${formatTimezoneAbbrevAndOffset(z)})`,
      value: z as string | 'browser',
      searchHaystack: getTimezoneSearchHaystack(z).toLowerCase(),
    }));
    const extra =
      timezoneOverride && !ZONE_OPTIONS.includes(timezoneOverride)
        ? [
            {
              id: timezoneOverride,
              label: `${timezoneOverride} (${formatTimezoneAbbrevAndOffset(timezoneOverride)})`,
              value: timezoneOverride as string | 'browser',
              searchHaystack: getTimezoneSearchHaystack(timezoneOverride).toLowerCase(),
            },
          ]
        : [];
    return [browserOption, ...extra, ...zones];
  }, [browserOption, timezoneOverride]);

  const selectedOption: TzAutocompleteOption =
    timezoneOverride === null
      ? browserOption
      : allOptions.find((o) => o.value === timezoneOverride) ?? {
          id: currentTimezone,
          label: `${currentTimezone} (${formatTimezoneAbbrevAndOffset(currentTimezone)})`,
          value: currentTimezone,
          searchHaystack: getTimezoneSearchHaystack(currentTimezone).toLowerCase(),
        };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="timezone-dialog-title">
      <DialogTitle id="timezone-dialog-title">Display timezone</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All dates and times use the selected zone; offsets are shown in UTC only. Search by IANA id, country, or
          city (e.g.{' '}
          <Link
            component="button"
            type="button"
            underline="always"
            sx={{ verticalAlign: 'baseline', font: 'inherit', cursor: 'pointer' }}
            onClick={() => setInput('India')}
          >
            India
          </Link>
          ,{' '}
          <Link
            component="button"
            type="button"
            underline="always"
            sx={{ verticalAlign: 'baseline', font: 'inherit', cursor: 'pointer' }}
            onClick={() => setInput('Jerusalem')}
          >
            Jerusalem
          </Link>
          ,{' '}
          <Link
            component="button"
            type="button"
            underline="always"
            sx={{ verticalAlign: 'baseline', font: 'inherit', cursor: 'pointer' }}
            onClick={() => setInput('London')}
          >
            London
          </Link>
          ).
        </Typography>
        <Autocomplete
          autoHighlight
          options={allOptions}
          groupBy={(option) => (option.value === 'browser' ? 'Default' : 'IANA timezones')}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          value={selectedOption}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          onChange={(_, option) => {
            if (!option) return;
            if (option.value === 'browser') {
              onSelect('browser');
            } else if (typeof option.value === 'string') {
              onSelect(option.value);
            }
            onClose();
          }}
          filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter(
              (o) => o.searchHaystack.includes(q) || o.label.toLowerCase().includes(q)
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone"
              placeholder="Search…"
              autoFocus
              margin="dense"
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
          ListboxProps={{ style: { maxHeight: 320 } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Active: <strong>{currentTimezone}</strong> ({formatTimezoneAbbrevAndOffset(currentTimezone)})
          {timezoneOverride === null ? ' — following browser' : ' — custom'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimezoneSelectorDialog;
